import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';

export interface SupportMessage {
  id: number;
  conversationId: number;
  authorType: 'user' | 'admin';
  authorUserId: number | null;
  authorName: string | null;
  authorEmail: string | null;
  body: string;
  meta: any;
  createdAt: string;
}

export interface SupportConversation {
  id: number;
  uuid: string;
  userId: number | null;
  name: string;
  email: string;
  phone: string | null;
  messageType: string | null;
  subject: string | null;
  status: 'open' | 'closed' | string;
  waitingOn: 'admin' | 'user' | null;
  lastMessageAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  latestMessage?: SupportMessage | null;
  messages?: SupportMessage[];
}

export interface PaginationMeta {
  current_page: number;
  from: number;
  last_page: number;
  links: Array<{ url: string | null; label: string; active: boolean }>;
  path: string;
  per_page: number;
  to: number;
  total: number;
}

export interface ApiResponse<T> {
  data: T;
  status: string;
  message?: string;
  links?: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta?: PaginationMeta;
}

export interface SupportConversationFilters {
  page?: number;
  per_page?: number;
  status?: string;
  waiting_on?: string;
  message_type?: string;
  search?: string;
}

const getConversations = async (filters: SupportConversationFilters = {}): Promise<ApiResponse<SupportConversation[]>> => {
  const response = await api.get('/admin/support/conversations', { params: filters });
  return response.data;
};

const getConversation = async (uuid: string): Promise<ApiResponse<SupportConversation>> => {
  const response = await api.get(`/admin/support/conversations/${uuid}`);
  return response.data;
};

const replyToConversation = async ({ uuid, message }: { uuid: string; message: string }): Promise<ApiResponse<{ message: SupportMessage }>> => {
  const response = await api.post(`/admin/support/conversations/${uuid}/reply`, { message });
  return response.data;
};

const closeConversation = async (uuid: string): Promise<ApiResponse<SupportConversation>> => {
  const response = await api.post(`/admin/support/conversations/${uuid}/close`);
  return response.data;
};

export const useSupportConversations = (filters: SupportConversationFilters = {}) => {
  return useQuery<ApiResponse<SupportConversation[]>>({
    queryKey: ['admin', 'support', 'conversations', filters],
    queryFn: () => getConversations(filters),
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useSupportConversation = (uuid?: string) => {
  return useQuery<ApiResponse<SupportConversation>>({
    queryKey: ['admin', 'support', 'conversations', uuid],
    queryFn: () => getConversation(uuid as string),
    enabled: !!uuid,
    staleTime: 5 * 1000,
  });
};

export const useSupportConversationActions = () => {
  const queryClient = useQueryClient();

  const replyMutation = useMutation({
    mutationFn: replyToConversation,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'support', 'conversations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'support', 'conversations', variables.uuid] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'notifications', 'summary'] });
    },
  });

  const closeMutation = useMutation({
    mutationFn: closeConversation,
    onSuccess: (_data, uuid) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'support', 'conversations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'support', 'conversations', uuid] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'notifications', 'summary'] });
    },
  });

  return {
    reply: (uuid: string, message: string) => replyMutation.mutateAsync({ uuid, message }),
    close: (uuid: string) => closeMutation.mutateAsync(uuid),
    isReplying: replyMutation.isPending,
    isClosing: closeMutation.isPending,
  };
};

